<template>
  <div>
    <b-modal
      ref="modal-operations-add-document-orders"
      :title="$t('Add Document Orders').toLocaleUpperCase()"
      title-tag="h4"
      modal-class="modal-primary"
      @hidden="close"
      size="xl"
      no-close-on-backdrop
      scrollable
    >
      <filter-slot
        :filter="filters"
        :filterPrincipal="filterPrincipal"
        :paginate="paginate"
        @reload="$refs['ref-table-add-document-orders'].refresh()"
        :showPagination="false"
        :showAdvancedFilters="false"
      >
        <template #table>
          <!-- selectable -->
          <!-- @row-selected="onRowSelected" -->
          <b-table
            select-mode="multi"
            ref="ref-table-add-document-orders"
            :per-page="paginate.perPage"
            :current-page="paginate.currentPage"
            sticky-header="50vh"
            small
            class="position-relative font-small-3"
            table-class="text-nowrap"
            :busy.sync="isBusy"
            :items="documentOrders"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            primary-key="district_id"
            :empty-text="$t('No matching records found')"
          >
            <template #head(row_selected)="data">
              <b-form-checkbox
                :value="true"
                :unchecked-value="false"
                @change="selectAll(data)"
                v-model="selectedAll"
              ></b-form-checkbox>
            </template>

            <template #cell(row_selected)="data">
              <b-form-checkbox
                :value="true"
                :unchecked-value="false"
                @change="checked"
                v-model="data.item.row_selected"
              >
              </b-form-checkbox>
            </template>

            <template #table-busy>
              <b-skeleton-table
                :headers="fields"
                :rows="paginate.perPage"
                :columns="fields.length"
                :table-props="{ bordered: true, striped: true }"
              />
            </template>

            <template #cell(load_date)="data">
              <div class="d-flex flex-column">
                <div class="d-flex">
                  <div class="text-right mr-1" style="width: 80px">
                    <span> {{ $t("Load") }} </span>
                  </div>
                  <feather-icon
                    icon="CalendarIcon"
                    size="17"
                    class="cursor-pointer text-warning"
                  />
                  <span style="margin-left: 5px">
                    {{ data.item.load_date | myGlobal }}
                  </span>
                </div>
                <div class="d-flex justify-content-center">
                  <th class="p-0" style="margin-top: 5px; width: 150px" />
                </div>
                <div class="d-flex" style="margin-top: 5px">
                  <div class="text-right mr-1" style="width: 80px">
                    <span> {{ $t("Delivery") }} </span>
                  </div>
                  <feather-icon
                    icon="CalendarIcon"
                    size="17"
                    class="cursor-pointer text-primary"
                  />
                  <span style="margin-left: 5px">
                    {{ data.item.required_delivery_date | myGlobal }}
                  </span>
                </div>
              </div>
            </template>

            <template #cell(transport_number)="data">
              <span class="">{{ data.item.transport_number }}</span>
            </template>

            <template #cell(service_order_code)="data">
              <span class="">{{ data.item.service_order_code }}</span>
            </template>

            <template #cell(remittance_number)="data">
              <span> {{ data.item.remittance_number }} </span>
            </template>

            <template #cell(quantity)="data">
              <div
                class="d-flex align-items-left justify-content-left flex-column"
              >
                <div class="mt-05" v-if="data.item.quantity">
                  <span class="text-success">{{
                    data.item.quantity == null ? 0 : data.item.quantity
                  }}</span>
                </div>
                <span v-else>-</span>
              </div>
            </template>

            <template #cell(weight)="data">
              <div
                class="d-flex align-items-left justify-content-left flex-column"
              >
                <div class="mt-05" v-if="data.item.weight">
                  <span class="text-info">{{
                    data.item.weight.toFixed(3)
                  }}</span>
                </div>
                <span v-else>-</span>
              </div>
            </template>

            <template #cell(volume)="data">
              <div
                class="d-flex align-items-left justify-content-left flex-column"
              >
                <div class="mt-05" v-if="data.item.volume">
                  <span class="text-primary">{{
                    data.item.volume.toFixed(3)
                  }}</span>
                </div>
                <span v-else>-</span>
              </div>
            </template>

            <template #cell(created_at)="data">
              <div
                class="d-flex align-items-left justify-content-left flex-column"
              >
                <div class="mt-05" v-if="data.item.required_delivery_date">
                  <tabler-icon
                    icon="TruckDeliveryIcon"
                    size="17"
                    class="text-primary cursor-pointer mr-1"
                    v-b-tooltip.hover.left="$t('DELIVERY DATE')"
                  />
                  <span class="text-primary">{{
                    data.item.required_delivery_date
                  }}</span>
                </div>
                <div class="mt-05" v-if="data.item.created_at">
                  <tabler-icon
                    icon="ClockIcon"
                    size="17"
                    class="text-info cursor-pointer mr-1"
                    v-b-tooltip.hover.left="$t('CREATED AT')"
                  />

                  <span class="text-info">{{
                    data.item.created_at | prettyDate
                  }}</span>
                </div>
                <div class="mt-05" v-if="data.item.created_by">
                  <tabler-icon
                    icon="UserIcon"
                    size="17"
                    class="text-primary cursor-pointer mr-1"
                    v-b-tooltip.hover.left="$t('CREATED BY')"
                  />

                  <span class="text-primary">{{ data.item.created_by }}</span>
                </div>
              </div>
            </template>
          </b-table>
        </template>
      </filter-slot>

      <b-card :bg-variant="isDarkSkin ? 'black' : 'white'" class="mb-0">
        <div class="d-flex justify-content-between">
          <div>
            {{ $t("Services selected") }} :
            <span class="text-primary"> {{ getTotalSelected }} Items </span>
          </div>
          <div>
            {{ $t("Quantity") }} :
            <span class="text-info"> {{ getTotalQuantitySelected }} </span>
          </div>
          <div>
            {{ $t("Volume") }} :
            <span class="text-primary"> {{ getTotalVolumeSelected }} M3 </span>
          </div>
          <div>
            {{ $t("Weight") }} :
            <span class="text-warning"> {{ getTotalWeightSelected }} </span>
          </div>
        </div>
      </b-card>

      <template #modal-footer>
        <b-button variant="outline-danger" @click="close()">
          {{ $t("Cancel") }}
        </b-button>

        <b-button
          :disabled="getTotalSelected == 0"
          variant="outline-primary"
          @click="sentDocuments"
        >
          {{ $t("Add") }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import modalMixin from "@/mixins/modal";
import Ripple from "vue-ripple-directive";
import DocumentOrdersService from "@/views/operations/views/document-orders/services/document-orders.service.js";
import { mapGetters } from "vuex";
import Fields from "@/views/operations/views/service-orders/data/document-orders.fields.js";
import Filters from "@/views/operations/views/service-orders/data/document-orders.filters.js";
import FilterSlot from "@/views/commons/filter-slot/FilterSlot.vue";

export default {
  name: "AddDocumentOrdersModal",
  mixins: [modalMixin],
  directives: {
    Ripple,
  },
  components: {
    FilterSlot,
  },
  props: {
    currentOrders: {
      default: [],
    },
  },
  data() {
    return {
      show: true,
      filters: Filters,
      fields: Fields,
      isBusy: false,
      sortBy: "",
      sortDesc: true,
      filterPrincipal: {
        type: "input",
        inputType: "text",
        placeholder: "Search...",
        model: "",
      },
      paginate: {
        from: 0,
        to: 0,
        of: 0,
        perPage: 50,
        currentPage: 1,
      },
      selectedAll: false,
      documentOrders: [],
    };
  },
  computed: {
    ...mapGetters({
      currentUser: "auth/currentUser",
    }),

    getTotalSelected() {
      return this.documentOrders.filter((document) => document.row_selected)
        .length;
    },

    getTotalQuantitySelected() {
      let quantity = 0;
      this.documentOrders.map((document) => {
        if (document.row_selected) quantity += document.quantity;
      });

      return quantity;
    },

    getTotalVolumeSelected() {
      let volume = 0;
      this.documentOrders.map((document) => {
        if (document.row_selected) volume += document.volume;
      });
      return parseFloat(volume.toFixed(3));
    },

    getTotalWeightSelected() {
      let weight = 0;
      this.documentOrders.map((document) => {
        if (document.row_selected) weight += document.weight;
      });
      return parseFloat(weight.toFixed(3));
    },
  },
  mounted() {
    this.toggleModal("modal-operations-add-document-orders");
  },
  async created() {
    this.addPreloader();
    try {
      await Promise.all([this.getDocumentsWithoutServices()]);
    } catch (error) {
      console.log(error);
    } finally {
      this.removePreloader();
    }
  },
  methods: {
    async getDocumentsWithoutServices() {
      try {
        this.addPreloader();

        const params = {
          page: null,
          perpage: null,
          order: 1,
          orderby: null,
          search_txt: this.filterPrincipal.model,
          status: 1,
          from: null,
          to: null,
        };

        const { data } = await DocumentOrdersService.getAvailableDocumentOrders(
          {}
        );
        if (this.currentOrders.length) {
          data.map((document) => {
            if (this.currentOrders.includes(document.document_id)) {
              document.row_selected = true;
            }
            return document;
          });
        }
        this.documentOrders = data;
      } catch (error) {
        throw error;
      }
    },

    selectAll(data) {
      this.documentOrders.map((element) => {
        if (this.selectedAll) {
          element.row_selected = true;
        } else {
          element.row_selected = false;
        }
        return element;
      });
    },

    checked(checked) {
      if (!checked) this.selectedAll = false;
    },

    sentDocuments() {
      const documents = this.documentOrders.filter(
        (document) => document.row_selected
      );
      this.$emit("add-document-orders", documents);
    },

    close() {
      this.$emit("close");
    },
  },
};
</script>

<style>
.mt-05 {
  margin-top: 0.5rem;
}
.dark-layout .table.b-table > tbody .b-table-row-selected.table-active td {
  background-color: #424141 !important;
}

.mw {
  min-width: 50px !important;
}

.sticky-data {
  position: sticky;
  bottom: 0;
  z-index: 1000;
}

.fs-1 {
  font-size: 1.2rem !important;
}

.mt-05 {
  margin-top: 0.5rem;
}
</style>